import { useConfigStore } from '@xantory/store';
import { useSyncExternalStore } from 'preact/compat';

import { resolveWsHealthUrl } from '../core/path';
import type { WsHealthEvent } from '../domains/iot/iot.types';

/**
 * Global device-liveness store — the ONE source of truth for online/offline across the app.
 *
 * Every surface that shows MC status (Microcontrollers, IoT Control, Mapper, …) reads the same
 * map fed by a single `/ws/health` socket. The socket is **ref-counted**: it opens on the first
 * mounted subscriber and closes when the last one unmounts, so it is active only while a page that
 * needs liveness is on screen (no always-on connection, no per-page duplicate sockets).
 *
 * Correlate each frame to a row by `microcontroller_id` (the stream is device-keyed, not
 * location-scoped). The last-known map is retained across a full unmount so a remount shows status
 * immediately; the socket delivers a fresh snapshot on reconnect, so the stale window is one RTT.
 */

export type LiveHealthMap = Record<string, WsHealthEvent>;

const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;

let map: LiveHealthMap = {};
const listeners = new Set<() => void>();
let subscriberCount = 0;
let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let attempt = 0;
let currentUrl = '';
let unsubConfig: (() => void) | null = null;

function emit(): void {
  for (const l of listeners) l();
}

function clearReconnect(): void {
  if (reconnectTimer != null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function connect(): void {
  let url: string;
  try {
    url = resolveWsHealthUrl();
  } catch {
    // e.g. cloud mode with no site selected — nothing to connect to yet; a config change re-tries.
    ws = null;
    return;
  }
  currentUrl = url;
  const sock = new WebSocket(url);
  ws = sock;

  sock.onopen = () => {
    attempt = 0;
  };

  sock.onmessage = (e: MessageEvent) => {
    try {
      const ev = JSON.parse(e.data) as WsHealthEvent;
      if (ev?.microcontroller_id) {
        map = { ...map, [ev.microcontroller_id]: ev };
        emit();
      }
    } catch {
      /* malformed frame */
    }
  };

  sock.onclose = () => {
    if (ws === sock) ws = null;
    if (subscriberCount === 0) return; // torn down on purpose (last subscriber left)
    const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)] ?? 30_000;
    attempt++;
    reconnectTimer = setTimeout(connect, delay);
  };

  sock.onerror = () => {
    sock.close();
  };
}

function start(): void {
  attempt = 0;
  connect();
  // The resolved URL is site/mode-dependent (cloud). This socket is long-lived across navigation,
  // so reconnect it if a site switch / mode change would change the target.
  unsubConfig = useConfigStore.subscribe(() => {
    let next = '';
    try {
      next = resolveWsHealthUrl();
    } catch {
      next = '';
    }
    if (next !== currentUrl) {
      clearReconnect();
      ws?.close();
      ws = null;
      attempt = 0;
      if (subscriberCount > 0) connect();
    }
  });
}

function stop(): void {
  clearReconnect();
  unsubConfig?.();
  unsubConfig = null;
  ws?.close();
  ws = null;
  // keep `map` (last-known) so a remount is not blank; reconnect refreshes it via the snapshot.
}

/** Ref-counted subscription: opens the socket on the first subscriber, closes it on the last. */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  subscriberCount++;
  if (subscriberCount === 1) start();
  return () => {
    listeners.delete(listener);
    subscriberCount--;
    if (subscriberCount === 0) stop();
  };
}

const getMap = (): LiveHealthMap => map;

/**
 * The whole live-health map, reactive. Mounting this hook keeps the shared `/ws/health` socket
 * open; unmounting the last consumer closes it. Pass the returned map to the page's adapter.
 */
export function useLiveHealthMap(): LiveHealthMap {
  return useSyncExternalStore(subscribe, getMap);
}

/**
 * A single controller's latest liveness frame, reactive — re-renders only when THAT controller
 * changes (the per-frame map spread keeps every other entry referentially stable). Use for a
 * single badge/dot without pulling the whole map.
 */
export function useLiveness(mcId: string | undefined): WsHealthEvent | undefined {
  return useSyncExternalStore(subscribe, () => (mcId ? map[mcId] : undefined));
}
